import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Every page uses the full width of the dialog (`wide`). `fill` hands the whole
 * body area to the page without padding or scroll.
 */
export type SettingsPageWidth = 'wide' | 'fill';

export interface SettingsTabItem {
  id: string;
  label: ReactNode;
  testId?: string;
  disabled?: boolean;
  /** Lets the owner bind the tab button, e.g. to a navigation catalog. */
  wrap?: (tab: ReactElement) => ReactElement;
}

export interface SettingsTabsProps {
  tabs: readonly SettingsTabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  ariaLabel?: string;
}

export interface SettingsFrame {
  /** Fixed area above the scrolling body; page headers render into it. */
  headerSlot: HTMLElement | null;
  /** The scrolling body, used by the "on this page" rail to follow sections. */
  scrollRoot: HTMLElement | null;
  /** Localized path of the open page, e.g. "Workspace · Acme · Members". */
  eyebrow: ReactNode;
  /**
   * Sub-tabs the dialog itself switches between, for sidebar entries that
   * combine several pages. A page that brings its own tabs shows those instead.
   */
  tabs: SettingsTabsProps | null;
  width: SettingsPageWidth;
  setWidth: (width: SettingsPageWidth) => void;
  /** Identifies the open sub-tab so the frame can keep a scroll position per view. */
  setView: (view: string | null) => void;
  /**
   * Called once the open page or sub-tab shows its content, so the dialog can
   * restore a scroll position the content was too short for before.
   */
  pageReady: () => void;
  /**
   * Registers a check for unsaved changes. The dialog asks before it leaves a
   * page while any registered check returns true.
   */
  registerUnsavedGuard: (hasUnsavedChanges: () => boolean) => () => void;
  /**
   * Whether the server is reachable. A page does not wait for server data while
   * it is not: local-first, it shows what it has.
   */
  backendAvailable: boolean;
}

const SettingsFrameContext = createContext<SettingsFrame | null>(null);

export const useSettingsFrame = () => useContext(SettingsFrameContext);

/**
 * Owned by the settings dialog. A page only declares its header and width; the
 * dialog decides where they are placed, so every page shares one layout.
 */
export const SettingsFrameProvider = ({
  headerSlot,
  scrollRoot,
  eyebrow,
  tabs = null,
  onViewChange,
  onPageReady,
  registerUnsavedGuard,
  backendAvailable,
  children,
}: {
  headerSlot: HTMLElement | null;
  scrollRoot: HTMLElement | null;
  eyebrow: ReactNode;
  tabs?: SettingsTabsProps | null;
  onViewChange: (view: string | null) => void;
  onPageReady: () => void;
  registerUnsavedGuard: SettingsFrame['registerUnsavedGuard'];
  backendAvailable: boolean;
  children: ReactNode;
}) => {
  const [width, setWidth] = useState<SettingsPageWidth>('wide');
  const frame = useMemo<SettingsFrame>(
    () => ({
      headerSlot,
      scrollRoot,
      eyebrow,
      tabs,
      width,
      setWidth,
      setView: onViewChange,
      pageReady: onPageReady,
      registerUnsavedGuard,
      backendAvailable,
    }),
    [
      headerSlot,
      scrollRoot,
      eyebrow,
      tabs,
      width,
      onViewChange,
      onPageReady,
      registerUnsavedGuard,
      backendAvailable,
    ]
  );
  return (
    <SettingsFrameContext.Provider value={frame}>
      {children}
    </SettingsFrameContext.Provider>
  );
};

/** Declares the width of the open page for as long as the caller is mounted. */
export const useSettingsPageWidth = (width: SettingsPageWidth) => {
  const setWidth = useSettingsFrame()?.setWidth;
  useLayoutEffect(() => {
    if (!setWidth) return;
    setWidth(width);
    return () => setWidth('wide');
  }, [setWidth, width]);
};
